import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { Panel } from '@gamma/ui/layout';
import { ErrorBoundary } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useReloadHumioUI } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import {
  DateRangeContext,
  LogScaleContext,
} from '@investigator/shared/providers';
import { hideHumioInnerNav } from '@investigator/shared/utils';
import moment from 'moment';
import { isSafari } from 'react-device-detect';
import { useLocation } from 'react-router';
import { HumioLoaderOverlay, HumioUnavailable } from '../../components';

const { humioSearch } = i18n.pages.humio;

export const HumioSearch = () => {
  const stableIframeSrcRef = useRef<string>('');

  const {
    iframeKey,
    logScaleDashboardsLoading,
    logScaleDashboardsError,
    logScaleDashboardsData,
    sessionIdTokenCorelight,
  } = useContext(LogScaleContext);

  const { dateRange = [7, 'days'] } = useContext(DateRangeContext);

  const { search } = useLocation();

  // Track if we're waiting for token refresh
  const [isWaitingForToken, setIsWaitingForToken] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showUnavailable, setShowUnavailable] = useState(false);

  const params = useMemo(() => {
    return new URLSearchParams(search);
  }, [search]);

  // Control when to show the unavailable component
  useEffect(() => {
    // Don't show unavailable while loading
    if (logScaleDashboardsLoading || isWaitingForToken) {
      setShowUnavailable(false);
      return;
    }

    // Show unavailable if there's an error after retries
    if (logScaleDashboardsError && retryCount >= 3) {
      setShowUnavailable(true);
      return;
    }

    // Show unavailable if data loaded successfully but no dashboards found
    if (
      !logScaleDashboardsError &&
      (logScaleDashboardsData?.getHumioDashboards?.searchDomain?.dashboards
        ?.length ?? 0) === 0
    ) {
      setShowUnavailable(true);
      return;
    }

    // Otherwise, don't show unavailable
    setShowUnavailable(false);
  }, [
    logScaleDashboardsLoading,
    logScaleDashboardsData,
    logScaleDashboardsError,
    isWaitingForToken,
    retryCount,
  ]);

  // Handle downstream errors
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (logScaleDashboardsError) {
      interface GraphQLErrorExtension {
        code?: string;
        [key: string]: unknown;
      }

      interface GraphQLError {
        extensions?: GraphQLErrorExtension;
        [key: string]: unknown;
      }

      interface LogScaleDashboardsError {
        graphQLErrors?: GraphQLError[];
        [key: string]: unknown;
      }

      const isDownstreamError = (
        logScaleDashboardsError as LogScaleDashboardsError
      ).graphQLErrors?.some(
        (err: GraphQLError) =>
          err.extensions?.code === 'DOWNSTREAM_SERVICE_ERROR',
      );

      if (isDownstreamError && retryCount < 3) {
        setIsWaitingForToken(true);
        setRetryCount((prev) => prev + 1);

        // Reset waiting state after a timeout
        timer = setTimeout(() => {
          setIsWaitingForToken(false);
        }, 5000);
      }
    } else {
      // Reset retry count on success
      setRetryCount(0);
      setIsWaitingForToken(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [logScaleDashboardsError, retryCount]);

  const end = moment(Number(params.get('humioEnd')));
  const roundUp =
    end.second() || end.millisecond()
      ? end.add(1, 'minute').startOf('minute')
      : end;

  params.set('humioEnd', String(roundUp.valueOf()));

  params.delete('start');
  params.delete('end');
  params.delete('referrer_type');
  params.delete('referrer_alert_url');
  params.delete('referrer_alert_name');
  params.delete('referrer_entity_url');
  params.delete('referrer_entity_name');

  const stringifyParams = useMemo(() => {
    if (params.get('humioStart') === '0') {
      params.set(
        'humioStart',
        String(moment().subtract(dateRange[0], dateRange[1]).valueOf()),
      );
    }
    if (params.get('humioEnd') === '0') {
      params.set('humioEnd', String(moment().valueOf()));
    }
    let stringifyParams = String(params);
    const startRegex = /humioStart/i;
    stringifyParams = stringifyParams.replace(startRegex, 'start');
    const endRegex = /humioEnd/i;
    stringifyParams = stringifyParams.replace(endRegex, 'end');
    return stringifyParams;
  }, [params, dateRange]);

  const { iframeRef, iframeSrc } = useReloadHumioUI({
    iframeType: 'search',
    params: stringifyParams,
  });
  // Capture first valid iframeSrc and never lose it
  if (iframeSrc && !stableIframeSrcRef.current) {
    stableIframeSrcRef.current = iframeSrc;
  }
  // Use the stable ref for rendering
  const effectiveIframeSrc = stableIframeSrcRef.current || iframeSrc;

  // Show unavailable component when state indicates it should be shown
  if (showUnavailable) {
    return (
      <HumioUnavailable
        unavailable={humioSearch.unavailable}
        techSupport={humioSearch.techSupport}
      />
    );
  }

  if (!effectiveIframeSrc || !sessionIdTokenCorelight) {
    return (
      <ErrorBoundary page="Search" styleClass="main">
        <Panel>
          <GraphQLReqStatus loading={true} />
        </Panel>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary page="Search" styleClass="main">
      <Box data-testid="humio-search" height="100%">
        {isSafari && <HumioLoaderOverlay reloadKey={iframeKey} />}

        {effectiveIframeSrc && sessionIdTokenCorelight && (
          <iframe
            title="ui"
            width="100%"
            height="100%"
            id="humio-ui"
            data-testid="humio-ui"
            onLoad={() => hideHumioInnerNav()}
            key={iframeKey}
            ref={iframeRef}
            src={effectiveIframeSrc}
          />
        )}
      </Box>
    </ErrorBoundary>
  );
};
